import { useMemo, useState } from "react";

import { COUNTRIES, countryByIso2, flagEmoji, splitE164 } from "./countries";
import { RequiredMark } from "./required-mark";
import { Select } from "./select";
import type { SelectOption } from "./select";
import styles from "./phone-input.module.css";

export interface PhoneInputProps {
  /** E.164, or "" for not yet entered. The only value this component reads or writes. */
  value: string;
  onChange: (value: string) => void;
  /**
   * ISO-3166 alpha-2 to prefill the country with when `value` is empty —
   * typically the clinic's own `defaultCountry`. Never applied once `value`
   * already carries a country (editing an existing number), and never a
   * guess when this is null: the picker simply starts unselected.
   */
  defaultCountry?: string | null;
  /** Stable ids for e2e tests: `${testIdPrefix}-country` and `${testIdPrefix}-number`. */
  testIdPrefix: string;
  /** Overrides the "Phone" label — the settings forms say what the number is for. */
  label?: string;
  /** Draws the error treatment on both halves. The message belongs to the form. */
  invalid?: boolean;
  /** Draws the red star on the number (see `ui/required-mark.tsx`). */
  required?: boolean;
}

/**
 * The country code as its own section — a compact select carrying the
 * country's flag and dial code, beside a plain field for the rest of the
 * number. Combines back into a single E.164 string on every change, which is
 * the only shape either API DTO (`CreatePatientDto`, patient update) accepts.
 */
export function PhoneInput({
  value,
  onChange,
  defaultCountry,
  testIdPrefix,
  label,
  invalid,
  required,
}: PhoneInputProps): React.JSX.Element {
  const countryOptions = useMemo<SelectOption[]>(
    () =>
      COUNTRIES.map((country) => ({
        value: country.iso2,
        label: `${flagEmoji(country.iso2)} ${country.name}`,
        keywords: `${country.iso2} ${country.dialCode}`,
        hint: country.dialCode,
      })),
    [],
  );

  const parsed = splitE164(value);
  const iso2 = parsed.iso2 ?? (value === "" && defaultCountry ? defaultCountry : "");
  const national = parsed.national;

  /**
   * **What was typed, including a leading zero** (client, 2026-09-07: *"make it
   * accept 03 phone numbers such as 03853045, it's a valid use case"*).
   *
   * The zero was stripped as the digit was pressed, so typing `03853045` showed
   * `3853045` and the `0` simply never appeared. The stored number was right —
   * `+9613853045`, which is the number — but a receptionist typing what is
   * printed on a card and watching the first digit vanish has every reason to
   * think the field refused it.
   *
   * So the trunk prefix is kept on screen and dropped only when composing
   * E.164, which is where it actually does not belong.
   *
   * Held as state and *derived back* rather than synced with an effect: the
   * typed form is shown only while it still agrees with the value in hand, so a
   * number arriving from anywhere else — loading a patient, resetting the form
   * — displays canonically without anything having to notice it changed.
   */
  const [typed, setTyped] = useState<string | null>(null);
  const shown = typed !== null && stripTrunk(typed) === national ? typed : national;

  const emit = (nextIso2: string, nextNational: string): void => {
    const dialCode = nextIso2 === "" ? "" : (countryByIso2(nextIso2)?.dialCode ?? "");
    onChange(`${dialCode}${stripTrunk(nextNational)}`);
  };

  return (
    <div className={styles.row}>
      <div className={styles.countryField}>
        {/*
          **Searchable, like every other dropdown** — two hundred countries is
          not a list anybody scrolls, and the native control's type-ahead
          matches the label's leading dial code rather than the name, so typing
          "leb" found nothing at all.

          The country reads first and the dial code second: people know which
          country they are in and look the code up, never the reverse.

          **"Country phone code", not "Country"** (client, 2026-09-06). It was
          briefly the latter, which put two fields called Country on the new
          location form — one asking where the practice is and one asking what
          to put in front of its number. They are different questions and the
          label has to say which is which.
        */}
        <Select
          id={`${testIdPrefix}-country`}
          label="Country phone code"
          value={iso2}
          options={countryOptions}
          onChange={(next) => emit(next, national)}
          testId={`${testIdPrefix}-country`}
          placeholder="🌐 Choose…"
          invalid={invalid}
        />
      </div>
      <label className={styles.numberField}>
        <span className={styles.label}>
          {label ?? "Phone"}
          {required === true && <RequiredMark />}
        </span>
        <input
          className={`${styles.input} ${invalid === true ? styles.invalid : ""}`}
          value={shown}
          inputMode="tel"
          autoComplete="tel-national"
          /**
           * **Digits only, enforced as it is typed** (client, 2026-09-05:
           * *"make sure i can only type numbers and not letters"*).
           *
           * Not validated on save — filtered on entry, so a letter, a space, a
           * dash or a bracket never appears in the box at all. A number typed
           * as `03 123 456` and refused afterwards is a worse experience than
           * one that could not be typed that way, and `phoneE164 must be an
           * E.164 number` is not a sentence a front desk can act on.
           *
           * The leading zero is **kept here and dropped in `emit`** — see the
           * note on `typed` above. A trunk prefix is exactly what E.164 strips,
           * and `+9610385…` is the commonest way to get this wrong, but the
           * place to strip it is on the way to the server rather than under the
           * cursor.
           */
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, "").slice(0, 15);
            setTyped(digits);
            emit(iso2, digits);
          }}
          placeholder="03000000"
          data-testid={`${testIdPrefix}-number`}
        />
      </label>

      {/*
        **Says which control to reach for, because the API's own message names
        one that is not here.**

        With no country selected `emit` composes a bare national number, and the
        server refuses it: *"a phone number must be entered in full
        international form, starting with +."* That is true of the API and
        impossible in this field — the `onChange` above filters every non-digit
        as it is typed, so the `+` the message asks for never lands. Typing it
        looked like the field silently eating the character, and the dropdown
        that does supply a dial code went unmentioned.

        Shown while typing rather than after a failed save, and only once there
        is a number for the question to be about: an empty field is not yet
        wrong.
      */}
      {iso2 === "" && shown !== "" && (
        <p className={styles.needsCountry} data-testid={`${testIdPrefix}-needs-country`}>
          Choose the country phone code as well — a number is stored with its country, and this one
          has none yet.
        </p>
      )}
    </div>
  );
}

/**
 * Drop a national trunk prefix, which E.164 has no room for.
 *
 * `03853045` is how a Lebanese mobile is written and said; `+9613853045` is
 * what it is. The zero is a dialling instruction for the domestic network, not
 * part of the number, so it belongs on the screen and not in storage.
 */
function stripTrunk(national: string): string {
  return national.replace(/^0+/, "");
}
